import type { NativeCapabilityDeclaration, NativeTypeDescription } from './native/hollowscript.js';
import type { StudioCapabilityName } from './capabilityNames.js';

/** `EntityRecord?` - `entities.get`'s own return type (`capabilities.ts`'s
 * `ENTITY_RECORD_TYPE`, `MapObjectInstance`'s shape), spelled as a native
 * type description. Representable at all only since
 * `sstk-hollowscript-lang` commit `f653b720ea5cf069af654a871250b9d6266ed838`
 * ("every transferable type in capability declarations") - before that,
 * `crates/hollowscript-wasm/src/tooling.rs`'s JSON parser accepted only the
 * four scalars plus `Result`, which is why an earlier version of this file
 * left `entities.get`/`input.moveAxis` out entirely (HS0320 on either -
 * see this repository's PR #61 for the minimal repro that was routed
 * upstream, and this file's own git history for that version). */
const ENTITY_RECORD_TYPE: NativeTypeDescription = {
  record: { id: 'String', assetId: 'String', floor: 'Int', x: 'Float', y: 'Float', z: 'Float' },
};

/**
 * The native VM's `check`/`compile` capability-interface declarations for
 * Studio's engine-bound capabilities (`print`/`clock`/`random` need no
 * declaration at all - the wasm tooling build already checks a program
 * against them by default, the same standard set the CLI grants with no
 * `--allow`; see `crates/hollowscript-wasm/web/README.md` in the language
 * repository). Every one of the eight v1 capabilities' functions is
 * declared here now, including `entities.get` and `input.moveAxis` (see
 * `ENTITY_RECORD_TYPE` above) - unlike `entities`, `input` is still never
 * dispatched by `workerCore.ts`'s `nativeDispatch`: `uses input` was never
 * wired through the tier-1 Worker's capability-call bridge in the first
 * place (`capabilities.ts`'s own `StudioCapabilityHandles.input` doc
 * comment - "separate follow-up work, not yet done"), a pre-existing gap
 * this change does not close. Declaring `input.moveAxis`'s real type here
 * only means a script that calls it now checks and compiles correctly
 * (proving the record-type declaration mechanism works for it too); it
 * still faults if actually called, exactly as before.
 */
export const NATIVE_CAPABILITY_DECLARATIONS: Readonly<Partial<Record<StudioCapabilityName, readonly NativeCapabilityDeclaration[]>>> = {
  entities: [
    { name: 'entities', function: 'spawn', params: ['String', 'Int', 'Float', 'Float', 'Float'], result: 'String' },
    { name: 'entities', function: 'get', params: ['String'], result: { optional: ENTITY_RECORD_TYPE } },
    { name: 'entities', function: 'setPosition', params: ['String', 'Float', 'Float', 'Float'] },
    { name: 'entities', function: 'remove', params: ['String'], result: 'Bool' },
  ],
  variables: [
    { name: 'variables', function: 'get', params: ['String'], result: 'Float' },
    { name: 'variables', function: 'set', params: ['String', 'Float'] },
    { name: 'variables', function: 'add', params: ['String', 'Float'] },
  ],
  audio: [{ name: 'audio', function: 'play', params: ['String', 'String'] }],
  logic: [
    { name: 'logic', function: 'announce', params: ['String'] },
    { name: 'logic', function: 'emit', params: ['String'] },
    { name: 'logic', function: 'giveItem', params: ['String', 'Int'] },
    { name: 'logic', function: 'takeItem', params: ['String', 'Int'] },
    { name: 'logic', function: 'winGame', params: [] },
    { name: 'logic', function: 'loseGame', params: [] },
    { name: 'logic', function: 'startQuest', params: ['String', 'String'] },
    { name: 'logic', function: 'completeQuest', params: ['String'] },
    { name: 'logic', function: 'failQuest', params: ['String'] },
  ],
  input: [
    { name: 'input', function: 'isActionDown', params: ['String'], result: 'Bool' },
    { name: 'input', function: 'moveAxis', result: { record: { x: 'Float', y: 'Float' } } },
  ],
};

/** Builds the flat declaration array `HollowScriptTooling.check`/`compile`
 * takes, for exactly the capabilities `granted` both asks for and this
 * boundary can represent (see this module's own doc comment for the two
 * that are left out regardless of `granted`). */
export function buildNativeCapabilityDeclarations(granted: ReadonlySet<StudioCapabilityName>): NativeCapabilityDeclaration[] {
  const declarations: NativeCapabilityDeclaration[] = [];
  for (const [name, entries] of Object.entries(NATIVE_CAPABILITY_DECLARATIONS) as [StudioCapabilityName, readonly NativeCapabilityDeclaration[]][]) {
    if (granted.has(name)) declarations.push(...entries);
  }
  return declarations;
}
